import * as React from "react";
import { Outlet, useNavigate } from "react-router-dom";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { DashboardLayout } from "@toolpad/core/DashboardLayout";
import { Account, SignOutButton } from "@toolpad/core/Account";
import { SessionContext } from "@toolpad/core/AppProvider";
import {
  Avatar,
  Divider,
  IconButton,
  Stack,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { hasPermission } from "../auth/RequirePermission3";
import { useState, useEffect, useCallback } from "react";
import * as services from "./../services/saleng";
import { jwtDecode } from "jwt-decode";

function PosAccountPopoverContent() {
  const session = React.useContext(SessionContext);
  const user = session?.user;

  if (!user) return null;

  return (
    <Stack>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
        <Avatar sx={{ width: 48, height: 48 }}>
          {user.name?.charAt(0)?.toUpperCase() || "U"}
        </Avatar>
        <Stack>
          <Typography variant="body2" fontWeight="bolder">
            {user.name}
          </Typography>
          <Typography variant="caption">{user.branchName}</Typography>
          <Typography variant="caption">ตำแหน่ง : {user.roleName}</Typography>
          <Typography variant="caption">{user.companyName}</Typography>
        </Stack>
      </Stack>
      <Divider />
      <Stack sx={{ p: 1 }}>
        <SignOutButton />
      </Stack>
    </Stack>
  );
}

function ToolbarAccount() {
  return (
    <Account
      slots={{
        popoverContent: PosAccountPopoverContent,
      }}
    />
  );
}

function ToolbarActions() {
  const navigate = useNavigate();

  const [branch, setBranch] = useState(localStorage.getItem("branchId") || "1");
  const [branchList, setBranchList] = useState([]);
  const getAccessToken = () => localStorage.getItem("accessToken") || null;

  const decodeToken = () => {
    const token = getAccessToken();
    if (!token) return null;
    try {
      return jwtDecode(token);
    } catch {
      return null;
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const shopResponse = await services.getShop();
      const decoded = decodeToken();
      const shopData = shopResponse.data.data;

      const updatedBranches = shopData.users
        .filter(
          (b) => b.id === decoded.id && b?.UserResponsibleBranch?.length > 0
        )
        .flatMap((b) =>
          b.UserResponsibleBranch.map((ubr) => ({
            id: ubr.branch?.id,
            name: ubr.branch?.name,
          }))
        );

      setBranchList(updatedBranches);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBranchChange = (e) => {
    const newBranch = e.target.value;
    setBranch(newBranch);
    localStorage.setItem("branchId", newBranch);
    window.location.reload();
  };

  if (branchList.length === 0) return (
    <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ width: "100%" }}>
      <IconButton
        onClick={() => window.dispatchEvent(new CustomEvent('toggle-appbar'))}
        sx={{ color: "white", ml: 1, bgcolor: "rgba(255,255,255,0.15)" }}
      >
        <KeyboardArrowUpIcon />
      </IconButton>
    </Stack>
  );

  return (
    <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ width: "100%" }}>
      {hasPermission(["switch_branches"]) && branchList.length > 0 && (
        <Box sx={{ minWidth: 160 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ color: "#fff" }}>สาขา</InputLabel>
            <Select
              value={branch}
              onChange={handleBranchChange}
              label="สาขา"
              sx={{
                color: "white",
                borderRadius: "12px",
                bgcolor: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(4px)",
                "& .MuiSvgIcon-root": { color: "white" },
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.25)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
            >
              {branchList.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      <IconButton
        onClick={() => window.dispatchEvent(new CustomEvent('toggle-appbar'))}
        sx={{ color: "white", ml: 'auto', bgcolor: "rgba(255,255,255,0.15)" }}
      >
        <KeyboardArrowUpIcon />
      </IconButton>
    </Stack>
  );

}

export default function Layout() {
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    const handleToggle = () => setIsCollapsed(prev => !prev);
    window.addEventListener('toggle-appbar', handleToggle);
    return () => window.removeEventListener('toggle-appbar', handleToggle);
  }, []);

  return (
    <Box sx={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>
      <Box
        sx={{
          width: '100%',
          height: isCollapsed ? 'calc(100vh + 64px)' : '100vh',
          transform: isCollapsed ? 'translateY(-64px)' : 'translateY(0)',
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <DashboardLayout
          hideNavigation
          slots={{
            toolbarActions: ToolbarActions,
            toolbarAccount: ToolbarAccount,
          }}
          sx={{
            height: '100%',
            "& .MuiAppBar-root": {
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              color: "#fff",
              borderBottom: "none",
              boxShadow: "0 4px 20px -5px rgba(16, 185, 129, 0.4)",
            }
          }}
        >
          <Outlet />
        </DashboardLayout>
      </Box>

      {/* Floating Expand Button when Appbar is collapsed */}
      {isCollapsed && (
        <IconButton
          onClick={() => setIsCollapsed(false)}
          sx={{
            position: 'fixed',
            top: 0,
            right: 16,
            zIndex: 9999,
            backgroundColor: '#10B981',
            color: 'white',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 16,
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            "&:hover": {
              backgroundColor: '#059669'
            },
            padding: '4px 16px',
          }}
        >
          <KeyboardArrowDownIcon />
        </IconButton>
      )}
    </Box>
  );
}
